// Phase 3.5 — Shop-level quantity pricing mode (per-unit vs grouped).
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, Save } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  useShopPricingSettings,
  useUpsertPricingSettings,
  useShopPricingConfig,
} from '@/hooks/pricing/useShopPricingConfig';
import { suggestedSellHybrid } from '@/lib/parts-financial/calculator';

interface Props { shopId: string; }

const fmt = (n: number) =>
  n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });

export function QuantityModeSection({ shopId }: Props) {
  const settingsQ = useShopPricingSettings(shopId);
  const upsert = useUpsertPricingSettings();
  const { config } = useShopPricingConfig(shopId);

  const [mode, setMode] = useState<'per_unit' | 'grouped'>('per_unit');

  useEffect(() => {
    if (!settingsQ.data) return;
    setMode(settingsQ.data.matrix_quantity_mode ?? 'per_unit');
  }, [settingsQ.data]);

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({
        shop_id: shopId,
        matrix_quantity_mode: mode,
      } as any);
      toast({ title: 'Quantity pricing mode saved' });
    } catch (e: any) {
      toast({ title: 'Save failed', description: e?.message, variant: 'destructive' });
    }
  };

  // Live spark-plug example: 4 × $10
  const exampleQty = 4;
  const exampleCost = 10;
  const perUnit = suggestedSellHybrid(exampleCost, null, config, {
    quantity: exampleQty, quantityMode: 'per_unit',
  });
  const grouped = suggestedSellHybrid(exampleCost, null, config, {
    quantity: exampleQty, quantityMode: 'grouped',
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Matrix Quantity Pricing Mode</CardTitle>
        <CardDescription>
          Controls how the matrix treats multi-quantity parts. Persisted per shop.
          Existing parts lines keep the mode that was active when they were priced.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <RadioGroup value={mode} onValueChange={(v) => setMode(v as any)} className="space-y-3">
          <div className="flex items-start gap-3 rounded-md border p-3">
            <RadioGroupItem value="per_unit" id="qty-per-unit" className="mt-0.5" />
            <div className="space-y-1">
              <Label htmlFor="qty-per-unit" className="font-medium cursor-pointer">
                Per-Unit
              </Label>
              <p className="text-xs text-muted-foreground">
                Apply matrix pricing to each part unit individually, then multiply by quantity.
                Stronger margin per unit on small repeated items.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-md border p-3">
            <RadioGroupItem value="grouped" id="qty-grouped" className="mt-0.5" />
            <div className="space-y-1">
              <Label htmlFor="qty-grouped" className="font-medium cursor-pointer">
                Grouped
              </Label>
              <p className="text-xs text-muted-foreground">
                Combine total cost across quantity first, then apply matrix pricing once.
                Smoother pricing on bundled items; less aggressive at the high tiers.
              </p>
            </div>
          </div>
        </RadioGroup>

        {/* Live example */}
        <div className="rounded-md border bg-muted/30 p-3 space-y-2">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Live example: {exampleQty} × {fmt(exampleCost)} part
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="space-y-0.5">
              <div className="text-xs text-muted-foreground">Per-Unit</div>
              <div className="font-medium tabular-nums">
                {fmt(perUnit.suggestedEach)} × {exampleQty} = {fmt(perUnit.suggestedTotal)}
              </div>
            </div>
            <div className="space-y-0.5">
              <div className="text-xs text-muted-foreground">Grouped</div>
              <div className="font-medium tabular-nums">
                {fmt(grouped.suggestedTotal)} total
                <span className="text-xs text-muted-foreground ml-1">
                  ({fmt(grouped.suggestedEach)}/ea display)
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={upsert.isPending} size="sm">
            {upsert.isPending
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Save className="h-4 w-4 mr-2" />}
            Save quantity mode
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
